type SendbirdUserTypeRecord = Record<SendbirdUserType, SendbirdUserType>;
const SendbirdUserTypeValue: SendbirdUserTypeRecord = {
  Patient: "Patient",
  Provider: "Provider",
  Support: "Support",
};

const IndividualSupportChannelPrefix = "SUP";

enum GroupChannelId {
  ACT = "ACT",
  CCT = "MAUD_CARETEAM",
  PatientAdmissions = "PT_ADMIT",
  PatientCct = "PT_CCT",
  PatientDietitian = "PT_DIETITIAN",
  PatientMedical = "PT_MEDICAL",
  PatientPeer = "PT_PEER",
  PatientProxyCct = "PT_PR_CCT",
  PatientPsych = "PT_PSYCH",
  PatientTherapist = "PT_THERAPIST",
  ProxyAdmissionSpecialist = "PR_AS",
  ProxyCct = "PR_CCT",
  ProxyDietitian = "PR_DIETITIAN",
  ProxyFamily = "PR_FAMILY",
  ProxyMedical = "PR_MEDICAL",
  ProxyPsych = "PR_PSYCH",
  ProxyTherapist = "PR_THERAPIST",
  SupportDietitian = "SUP_DIETITIAN",
  SupportFamily = "SUP_FAMILY",
  SupportMedical = "SUP_MEDICAL",
  SupportPsych = "SUP_PSYCH",
  SupportTherapist = "SUP_THERAPIST",
}

enum ChannelTypes {
  AdmissionSpecialist = "Admissions Specialist",
  CaregiverCoach = "Caregiver Coach",
  Dietitian = "Dietitian",
  FamilyMentor = "Family Mentor",
  MedicalTeam = "Medical Team",
  Other = "Other",
  PatientAndRecoveryCoach = "Patient + Recovery Coach",
  PatientAndSupportsAndProviderTeam = "+ Supports + Provider Team",
  PatientAndSupportsTherapyRDCoachingTeam = "+ Supports + Therapy, RD, Coaching Team",
  PatientTherapyRDCoachingTeam = "Therapy, RD, Coaching Team",
  PeerMentor = "Peer Mentor",
  ProviderTeam = "Provider Team",
  ProxyAndAdmissionsTeam = "Supports + Admissions Team",
  ProxyAndCaregiverCoach = "Supports + Caregiver Coach",
  ProxyAndDietitian = "Supports + Dietitian",
  ProxyAndFamilyMentor = "Supports + Family Mentor",
  ProxyAndMedicalTeam = "Supports + Medical Team",
  ProxyAndPsychTeam = "Supports + Psychiatry Team",
  ProxyAndTherapist = "Supports + Therapist",
  ProxyTherapyRDCoachingTeam = "Supports + Therapy, RD, Coaching Team",
  PsychiatryTeam = "Psychiatry Team",
  RecoveryCoach = "Recovery Coach",
  SupportsAndProviderTeam = "Supports + Provider Team",
  SupportsAndTeamOrRole = "Supports + #teamOrRole",
  Therapist = "Therapist",
  Unknown = "Equip",
}

// Prefer GroupChannel prefix over GroupChannelId for readability and explicitness
enum GroupChannelPrefix {
  ACT = "ACT",
  ClinicalCareTeam = "CCT",
  PatientAndProxyToCareTeam = "PT_PR_CCT",
  PatientToAdmissions = "PT_ADMIT",
  PatientToClinicalCareTeam = "PT_CCT",
  ProxyAndDietitian = "PR_DIETITIAN",
  ProxyAndFamilyMentors = "PR_FAMILY",
  ProxyAndMedicalTeam = "PR_MEDICAL",
  ProxyAndPsychiatryTeam = "PR_PSYCH",
  ProxyAndTherapist = "PR_THERAPIST",
  ProxyToAdmissions = "PR_AS",
  ProxyToClinicalCareTeam = "PR_CCT",
  SupportAndDietitian = "SUP_DIETITIAN",
  SupportAndFamilyMentors = "SUP_FAMILY",
  SupportAndPsychiatryTeam = "SUP_PSYCH",
  SupportAndTherapist = "SUP_THERAPIST",
}

const GroupChannelURLPrefixes: GroupChannelPrefix[] = [
  GroupChannelPrefix.ClinicalCareTeam,
  GroupChannelPrefix.ACT,
  GroupChannelPrefix.ProxyToAdmissions,
  GroupChannelPrefix.ProxyAndFamilyMentors,
  GroupChannelPrefix.ProxyAndTherapist,
  GroupChannelPrefix.ProxyAndDietitian,
  GroupChannelPrefix.ProxyAndMedicalTeam,
  GroupChannelPrefix.ProxyAndPsychiatryTeam,
  GroupChannelPrefix.ProxyToClinicalCareTeam,
  GroupChannelPrefix.PatientToClinicalCareTeam,
  GroupChannelPrefix.PatientAndProxyToCareTeam,
];

const InformationalModalGroupChannelPrefixes: GroupChannelPrefix[] = [
  GroupChannelPrefix.ProxyToClinicalCareTeam,
  GroupChannelPrefix.ProxyAndFamilyMentors,
  GroupChannelPrefix.ProxyAndTherapist,
  GroupChannelPrefix.ProxyAndDietitian,
  GroupChannelPrefix.ProxyAndMedicalTeam,
  GroupChannelPrefix.ProxyAndPsychiatryTeam,
  GroupChannelPrefix.PatientToClinicalCareTeam,
  GroupChannelPrefix.PatientAndProxyToCareTeam,
];

// Imported from MyEquip Web
const ChannelClassification = [
  {
    type: ChannelTypes.ProxyAndAdmissionsTeam,
    url: GroupChannelId.ProxyAdmissionSpecialist,
  },
  { type: ChannelTypes.ProxyAndFamilyMentor, url: GroupChannelId.ProxyFamily },
  { type: ChannelTypes.ProxyAndTherapist, url: GroupChannelId.ProxyTherapist },
  { type: ChannelTypes.ProxyAndDietitian, url: GroupChannelId.ProxyDietitian },
  { type: ChannelTypes.ProxyAndMedicalTeam, url: GroupChannelId.ProxyMedical },
  { type: ChannelTypes.ProxyAndPsychTeam, url: GroupChannelId.ProxyPsych },
  { type: ChannelTypes.ProviderTeam, url: GroupChannelId.PatientCct },
  { type: ChannelTypes.ProviderTeam, url: GroupChannelId.ProxyCct },
  {
    type: ChannelTypes.PatientAndSupportsAndProviderTeam,
    url: GroupChannelId.PatientProxyCct,
  },
  {
    type: ChannelTypes.AdmissionSpecialist,
    url: GroupChannelId.PatientAdmissions,
  },
  { type: ChannelTypes.Dietitian, url: GroupChannelId.PatientDietitian },
  { type: ChannelTypes.PeerMentor, url: GroupChannelId.PatientPeer },
  { type: ChannelTypes.Therapist, url: GroupChannelId.PatientTherapist },
  { type: ChannelTypes.MedicalTeam, url: GroupChannelId.PatientMedical },
  { type: ChannelTypes.PsychiatryTeam, url: GroupChannelId.PatientPsych },
  { type: ChannelTypes.Dietitian, url: GroupChannelId.SupportDietitian },
  { type: ChannelTypes.FamilyMentor, url: GroupChannelId.SupportFamily },
  { type: ChannelTypes.Therapist, url: GroupChannelId.SupportTherapist },
  { type: ChannelTypes.MedicalTeam, url: GroupChannelId.SupportMedical },
  { type: ChannelTypes.PsychiatryTeam, url: GroupChannelId.SupportPsych },
];

const SendbirdPushTestToolChannelUrlPrefix = "pushtesttool";
const EatingDisorderTreatmentArticleUrl =
  "https://equip.health/articles/treatment-and-recovery/multidisciplinary-eating-disorder-treatment";

const MentorRoleEvolutionChannelNames: Partial<
  Record<GroupChannelId, ChannelTypes>
> = {
  [GroupChannelId.PatientCct]: ChannelTypes.PatientTherapyRDCoachingTeam,
  // Peer Mentor / Recovery Coach channels
  [GroupChannelId.PatientPeer]: ChannelTypes.RecoveryCoach,
  [GroupChannelId.PatientProxyCct]:
    ChannelTypes.PatientAndSupportsTherapyRDCoachingTeam,
  // Clinical Care Team / Therapy, RD, Coaching Team channels
  [GroupChannelId.ProxyCct]: ChannelTypes.ProxyTherapyRDCoachingTeam,
  // Family Mentor / Caregiver Coach channels
  [GroupChannelId.ProxyFamily]: ChannelTypes.ProxyAndCaregiverCoach,
  [GroupChannelId.SupportFamily]: ChannelTypes.CaregiverCoach,
};

export {
  ChannelClassification,
  ChannelTypes,
  EatingDisorderTreatmentArticleUrl,
  GroupChannelId,
  GroupChannelPrefix,
  GroupChannelURLPrefixes,
  IndividualSupportChannelPrefix,
  InformationalModalGroupChannelPrefixes,
  MentorRoleEvolutionChannelNames,
  SendbirdPushTestToolChannelUrlPrefix,
  SendbirdUserTypeValue,
};
